import React, { useState, useEffect } from "react";
import { IMG_URL } from "../../config/config";
import AddToCartConfirmation from "./AddToCartConfirmation";
import { useCart } from "../../context/CartContext";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom"; // Add for navigation

const ProductDetailsModal = ({ show, onClose, product }) => {
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationType, setConfirmationType] = useState("added");
  const [selectedImage, setSelectedImage] = useState("");

  const [quantity, setQuantity] = useState(1);

  const { addToCart, cartItems } = useCart();
  const navigate = useNavigate(); // For programmatic navigation

  useEffect(() => {
    if (product && product.product_type === "configuration") {
      const rates = product.final_rate ? JSON.parse(product.final_rate) : [];
      if (rates.length) {
        setSelectedAttributes((prev) => ({
          ...prev,
          [product.product_id]: rates[0].attribute,
        }));
      }
    }
  }, [product, show]);
  useEffect(() => {
    if (product?.size) {
      setSelectedAttributes((prev) => ({
        ...prev,
        [product.product_id]: product.size,
      }));
    }
  }, [product]);

  // 🔹 Create image list safely
  const imageList = [product?.image, product?.image2, product?.image3].filter(
    Boolean,
  );

  // 🔹 useEffect ALWAYS at top level
  useEffect(() => {
    if (imageList.length > 0) {
      setSelectedImage(imageList[0]);
    }
  }, [product?.image, product?.image2, product?.image3]);

  // 🔹 NOW early return
  if (!show || !product) return null;

  const rates = product.final_rate ? JSON.parse(product.final_rate) : [];
  const isConfigurable =
    product.product_type === "configuration" || !!product.size;

  const selectedAttr = isConfigurable
    ? selectedAttributes[product.product_id] ||
      product.size ||
      rates[0]?.attribute
    : product.size || "UNI";

  const selectedRate = isConfigurable
    ? rates.find((r) => r.attribute === selectedAttr)
    : null;

  let price = "N/A";

  // Configurable
  if (isConfigurable && selectedRate) {
    price = parseFloat(selectedRate.finalRate);
  }
  // Simple product
  else if (!isConfigurable && rates.length) {
    const finalAmount =
      rates[0].finalAmount || rates[0].final_rate || rates[0].price;
    price = parseFloat(finalAmount);
  }
  // Fallback for CART product
  else if (product.price) {
    price = parseFloat(product.price);
  }

  const handleAttributeChangeCustom = (attribute) => {
    setSelectedAttributes((prev) => ({
      ...prev,
      [product.product_id]: attribute,
    }));
  };

  const handleQuantityChange = (delta) => {
    setQuantity((prev) => Math.max(1, prev + delta));
  };

  const handleAddToCart = async () => {
    if (isConfigurable && !selectedAttr) {
      toast.error("Please select a size!", {
        position: "bottom-right",
        autoClose: 2000,
      });
      return;
    }

    const result = await addToCart(
      {
        product_id: product.product_id,
        product_name: product.product_name,
        price,
        image: product.image,
        category_name: product.category_name,
      },
      quantity,
      selectedAttr,
    );

    if (result?.status === "exists") {
      setConfirmationType("exists");
      setShowConfirmation(true);
    } else if (result?.status === "added") {
      setConfirmationType("added");
      setShowConfirmation(true);
    }
  };

  // ✅ Check if product with selected size already in cart
  const isInCart = cartItems.some(
    (item) =>
      item.product_id === product.product_id &&
      (item.size || "").trim() === selectedAttr.trim(),
  );

  return (
    <div
      className="modal d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
    >
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content border-0 rounded-0">
          <div className="modal-body p-0 d-flex flex-wrap">
            {/* Left Images */}
            <div className="col-lg-7 d-flex">
              <div
                className="bg-light d-flex flex-column justify-content-start align-items-center p-3"
                style={{ width: "100px" }}
              >
                {/* {[1, 2, 3].map((_, i) => (
                  <img
                    key={i}
                    src={`${IMG_URL}/uploads/${product.image}`}
                    alt={`thumb-${i}`}
                    className="img-thumbnail mb-2"
                    style={{
                      width: "70px",
                      height: "auto",
                      objectFit: "cover",
                      cursor: "pointer",
                    }}
                  />
                ))} */}

                {imageList.map((img, i) => (
                  <img
                    key={i}
                    src={`${IMG_URL}/uploads/${img}`}
                    alt={`thumb-${i}`}
                    className="img-thumbnail mb-2"
                    style={{
                      width: "70px",
                      objectFit: "cover",
                      cursor: "pointer",
                      border:
                        selectedImage === img
                          ? "2px solid black"
                          : "1px solid #ccc",
                    }}
                    onClick={() => setSelectedImage(img)}
                  />
                ))}
              </div>
              <div className="flex-grow-1 d-flex justify-content-center p-4">
                <img
                  src={`${IMG_URL}/uploads/${selectedImage || product.image}`}
                  className="img-fluid"
                  alt="main"
                  style={{ maxHeight: "600px", objectFit: "contain" }}
                />
              </div>
            </div>

            {/* Right Info */}
            <div className="col-lg-5 p-5">
              <h3 className="fw-bold mb-2">{product.product_name}</h3>
              <h4 className="fw-semibold mb-4">
                ₹{price !== "N/A" ? price.toFixed(2) : "N/A"}
              </h4>
              <p className="text-muted mb-4">{product.description}</p>

              {/* Size */}
              <div className="mb-3">
                {isConfigurable ? (
                  <div className="size-options d-flex">
                    <p className="text-secondary me-2">Size</p>
                    {rates.length > 0 ? (
                      rates.map((rateObj, idx) => (
                        <button
                          key={idx}
                          className={`size-circle ${selectedAttr === rateObj.attribute ? "active" : ""}`}
                          onClick={() =>
                            handleAttributeChangeCustom(rateObj.attribute)
                          }
                        >
                          {rateObj.attribute}
                        </button>
                      ))
                    ) : (
                      <button className="size-circle active" disabled>
                        {selectedAttr}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="size-options d-flex">
                    <p className="text-secondary me-2">Size</p>
                    <button className="size-circle active" disabled>
                      UNI
                    </button>
                  </div>
                )}
              </div>

              {/* Add to Cart / Go to Cart */}
              {isInCart ? (
                <button
                  className="btn  btn-dark w-100 mb-4 p-2"
                  onClick={() => {
                    onClose(); // close modal
                    navigate("/cart"); // go to cart
                  }}
                >
                  GO TO CART
                </button>
              ) : (
                <button
                  className="btn btn-dark w-100 mb-4 p-2"
                  style={{ border: "none" }}
                  onClick={handleAddToCart}
                >
                  ADD TO CART
                </button>
              )}
            </div>

            <button
              type="button"
              className="btn-close position-absolute top-0 end-0 m-3"
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>

          <AddToCartConfirmation
            show={showConfirmation}
            productName={product.product_name}
            onClose={() => setShowConfirmation(false)}
            confirmationType={confirmationType}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductDetailsModal;
